import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  HeartHandshake,
  BookOpen,
  Sprout,
  Users,
  Sun,
  ArrowRight,
  Leaf,
} from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const KineticLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const FOCUS_AREAS = [
  {
    icon: HeartHandshake,
    title: "Mental Wellness",
    text: "Encouraging awareness, emotional well-being and healthier approaches to everyday challenges.",
    tint: "bg-nh-mint text-nh-green",
  },
  {
    icon: BookOpen,
    title: "Learning & Education",
    text: "Encouraging curiosity, learning and opportunities for educational and personal development.",
    tint: "bg-sky-50 text-nh-blue",
  },
  {
    icon: Sprout,
    title: "Personal Growth",
    text: "Supporting self-awareness, confidence, positive habits and continuous growth.",
    tint: "bg-nh-mint text-nh-green",
  },
  {
    icon: Users,
    title: "Community Support",
    text: "Creating opportunities for people to connect, participate and support one another.",
    tint: "bg-amber-50 text-nh-gold",
  },
  {
    icon: Sun,
    title: "Positive Living",
    text: "Encouraging balanced, meaningful and positive approaches to everyday life.",
    tint: "bg-amber-50 text-nh-gold",
  },
];

const PILLARS = [
  { n: "01", icon: BookOpen, title: "Learn", text: "Discover, understand and grow through learning." },
  { n: "02", icon: Sprout, title: "Grow", text: "Build confidence, awareness and positive habits." },
  { n: "03", icon: Users, title: "Connect", text: "Create meaningful relationships and stronger communities." },
];

export default function Home() {
  const { scrollY } = useScroll();
  const bannerY = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <>
      <SEO
        title="Nurturing Happiness | Mental Wellness, Learning & Personal Growth"
        description="Nurturing Happiness promotes mental wellness, learning, personal growth, positive living and stronger community connections."
      />

      <section data-testid="home-hero" className="relative">
        <div className="relative h-[46vh] min-h-[300px] overflow-hidden md:h-[76vh]">
          <motion.img
            src="/banner.webp"
            alt="Nurturing Happiness — Better Learning, Brighter Futures, Happier Communities"
            style={{ y: bannerY }}
            className="h-[115%] w-full object-cover object-center"
            fetchpriority="high"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-nh-cream to-transparent"
          />
        </div>

        <div className="relative z-10 mx-auto -mt-24 max-w-7xl px-5 md:-mt-36 md:px-12">
          <div className="max-w-3xl rounded-[2rem] border border-nh-deep/5 bg-white p-7 shadow-lift sm:p-10 md:p-14">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.25em] text-nh-green"
              data-testid="hero-overline"
            >
              <Sprout className="h-4 w-4" strokeWidth={2} />
              Nurturing Happiness
            </motion.p>
            <h1 className="mt-4 font-heading text-4xl font-bold leading-[1.12] tracking-tight text-nh-ink sm:text-5xl lg:text-[3.4rem]">
              <KineticLine delay={0.25}>Nurturing minds.</KineticLine>
              <KineticLine delay={0.38}>
                <span className="text-nh-green">Empowering learning.</span>
              </KineticLine>
              <KineticLine delay={0.51}>Building happier communities.</KineticLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.6 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-nh-muted md:text-lg"
              data-testid="hero-supporting-text"
            >
              We believe that mental well-being, learning, personal growth and supportive
              communities can help people lead more balanced and meaningful lives.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to="/about"
                data-testid="hero-learn-more-btn"
                className="rounded-full bg-nh-green px-8 py-3.5 text-[15px] font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-nh-deep hover:shadow-lift active:scale-95"
              >
                Learn More
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="rounded-full border-2 border-nh-deep/15 bg-white px-8 py-3.5 text-[15px] font-semibold text-nh-deep transition-all duration-300 hover:-translate-y-0.5 hover:border-nh-green hover:text-nh-green active:scale-95"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="home-welcome" className="paper-texture py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:grid-cols-2 md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">Welcome</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-nh-ink sm:text-4xl">
              Welcome to Nurturing Happiness
            </h2>
            <p className="mt-6 text-base leading-relaxed text-nh-muted md:text-lg">
              Nurturing Happiness is an initiative focused on mental wellness, learning, personal
              growth and positive community connections. We aim to encourage awareness, share
              meaningful resources and create opportunities for people to learn, connect and grow.
            </p>
            <div className="mt-8 flex items-center gap-3 text-nh-green">
              <Leaf className="h-5 w-5" />
              <span className="font-heading text-lg italic">Learn. Grow. Connect.</span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-3xl bg-nh-fresh/30" />
              <img
                src="https://images.unsplash.com/photo-1695795910772-6336b0beba36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3ZWxsbmVzcyUyMGNhbG0lMjBuYXR1cmV8ZW58MHx8fHwxNzkwMjQ3MTIxfDA&ixlib=rb-4.1.0&q=85"
                alt="A woman meditating peacefully on a rock beside a misty river in the Indian hills"
                loading="lazy"
                className="relative rounded-3xl object-cover shadow-soft md:aspect-[4/3] md:w-full"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="home-focus" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">Our Focus</p>
            <h2 className="mt-3 max-w-2xl font-heading text-3xl font-bold tracking-tight text-nh-ink sm:text-4xl">
              Five areas where happiness takes root
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FOCUS_AREAS.map((area, i) => (
              <Reveal key={area.title} delay={i * 0.08}>
                <div
                  data-testid={`focus-card-${i + 1}`}
                  className="group h-full rounded-3xl border border-nh-deep/5 bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:border-nh-green/15 hover:shadow-lift md:p-9"
                >
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ring-nh-deep/5 transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110 ${area.tint}`}
                  >
                    <area.icon className="h-7 w-7" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-nh-ink">{area.title}</h3>
                  <span
                    aria-hidden="true"
                    className="mt-3 block h-[3px] w-9 rounded-full bg-nh-gold/70 transition-all duration-500 group-hover:w-16 group-hover:bg-nh-gold"
                  />
                  <p className="mt-4 text-[15px] leading-relaxed text-nh-muted">{area.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <div className="flex h-full flex-col justify-between rounded-3xl bg-nh-green p-8 text-white shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift md:p-9">
                <Sprout className="h-9 w-9 text-nh-yellow" strokeWidth={1.75} />
                <div>
                  <p className="font-heading text-2xl font-semibold leading-snug">
                    Happiness grows when we grow together.
                  </p>
                  <Link
                    to="/about"
                    data-testid="focus-about-link"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-nh-yellow transition-colors hover:text-white"
                  >
                    Discover our story <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      <section data-testid="home-approach" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">How We Work</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-nh-ink sm:text-4xl">
              Our Approach
            </h2>
            <p className="mt-5 text-base leading-relaxed text-nh-muted md:text-lg">
              We believe happiness grows when people have the opportunity to learn, feel supported,
              develop themselves and build meaningful connections with others.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.12}>
                <div
                  data-testid={`pillar-${p.title.toLowerCase()}`}
                  className="group relative h-full overflow-hidden rounded-3xl border border-nh-deep/5 bg-white p-10 pt-12 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift"
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-3 right-6 select-none font-heading text-8xl font-bold text-nh-fresh/30 transition-colors duration-500 group-hover:text-nh-gold/40"
                  >
                    {p.n}
                  </span>
                  <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-nh-mint text-nh-green transition-colors duration-300 group-hover:bg-nh-green group-hover:text-white">
                    <p.icon className="h-7 w-7" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-7 font-heading text-3xl font-bold uppercase tracking-wide text-nh-deep">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-nh-muted">{p.text}</p>
                  <div className="absolute bottom-0 left-0 h-1.5 w-0 bg-nh-gold transition-all duration-500 group-hover:w-full" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-featured-article" className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:grid-cols-2 md:px-12">
          <Reveal>
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full rounded-3xl bg-nh-yellow/40" />
              <img
                src="https://images.unsplash.com/photo-1692269725911-87697c558be1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBjaGlsZHJlbiUyMHNjaG9vbHxlbnwwfHx8fDE3OTAyNDcxMjF8MA&ixlib=rb-4.1.0&q=85"
                alt="Two Indian schoolgirls in uniform reading and writing at their classroom desk"
                loading="lazy"
                className="relative rounded-3xl object-cover shadow-soft md:aspect-[4/3] md:w-full"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">
              Featured Article
            </p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-nh-ink sm:text-4xl">
              🌱 10 Tips for Better Education
            </h2>
            <p className="mt-6 text-base leading-relaxed text-nh-muted">
              Education is not only about examinations, grades and academic achievement. True
              education helps children and young people develop knowledge, confidence, curiosity,
              relationships, values and the ability to live meaningful and fulfilling lives.
            </p>
            <p className="mt-4 text-base leading-relaxed text-nh-muted">
              At Nurturing Happiness, we believe that learning becomes more powerful when learners
              feel supported, respected, motivated and happy.
            </p>
            <Link
              to="/10-tips-for-better-education"
              data-testid="featured-article-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-nh-green px-8 py-3.5 text-[15px] font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-nh-deep hover:shadow-lift active:scale-95"
            >
              Read the Full Article <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="home-final-cta" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-nh-deep px-8 py-16 text-center shadow-lift md:px-16 md:py-24">
              <Leaf className="pointer-events-none absolute -left-8 -top-8 h-40 w-40 text-nh-fresh/15" strokeWidth={1} />
              <Leaf className="pointer-events-none absolute -bottom-10 -right-6 h-48 w-48 rotate-45 text-nh-fresh/15" strokeWidth={1} />
              <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Let&apos;s Nurture Happiness Together
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-nh-cream/80 md:text-lg">
                Explore our work and discover ideas that encourage learning, personal growth,
                well-being and stronger communities.
              </p>
              <Link
                to="/what-we-do"
                data-testid="final-cta-btn"
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-nh-gold px-9 py-3.5 text-[15px] font-semibold text-nh-ink shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-nh-yellow hover:shadow-lift active:scale-95"
              >
                Explore What We Do <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
